import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { STATUS_LIST, STATUS_META, GEWERKE, gewerkRu, fmtEur } from "@/lib/domain";
import { StatusBadge, Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MapPin, Search, HardHat, AlertTriangle } from "lucide-react";

export default function Objects() {
  const [objects, setObjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [directoryError, setDirectoryError] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    setDirectoryError(null);
    try {
      const r = await api.get("/objects");
      setObjects(r.data);
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Не удалось загрузить объекты");
    } finally {
      setLoading(false);
    }
    if (isAdmin) {
      const [clientsResult, workersResult] = await Promise.allSettled([
        api.get("/clients"),
        api.get("/workers"),
      ]);
      if (clientsResult.status === "fulfilled") setClients(clientsResult.value.data);
      if (workersResult.status === "fulfilled") setWorkers(workersResult.value.data);
      const failed = [clientsResult, workersResult].filter((x) => x.status === "rejected");
      if (failed.length) {
        setDirectoryError("Справочники для создания объекта загружены не полностью. Список объектов доступен.");
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = objects.filter((o) => {
    if (filter !== "all" && o.status !== filter) return false;
    if (search && !`${o.title} ${o.city} ${o.address}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400/80 mb-2 font-medium">Стройплощадки</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Объекты</h1>
          <p className="text-slate-400 mt-1 text-sm">{objects.length} стройплощадок в системе</p>
        </div>
        {isAdmin && (
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-object-button" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                <Plus size={16} /> Новый объект
              </Button>
            </DialogTrigger>
            <CreateObjectDialog
              clients={clients}
              workers={workers}
              onCreated={() => { setOpenCreate(false); load(); }}
            />
          </Dialog>
        )}
      </div>

      {loadError && (
        <Card className="bg-red-500/10 border-red-500/30 p-4 text-sm text-red-300 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><AlertTriangle size={16} /> {loadError}</span>
          <Button size="sm" variant="outline" onClick={load} className="bg-transparent border-red-500/30 hover:bg-red-500/10">Повторить</Button>
        </Card>
      )}
      {directoryError && (
        <Card className="bg-amber-500/10 border-amber-500/30 p-3 text-xs text-amber-200">{directoryError}</Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="objects-search-input"
            placeholder="Поиск по названию, городу, адресу…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-[#131924] border-[#212b3d] h-10"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">Все</FilterChip>
          {STATUS_LIST.map((s) => (
            <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)} testId={`filter-${s}`}>
              {STATUS_META[s].label_ru}
            </FilterChip>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading && (
          <Card className="bg-[#131924] border-[#212b3d] p-10 col-span-full text-center text-slate-500">
            Загрузка объектов…
          </Card>
        )}
        {!loading && !loadError && filtered.length === 0 && (
          <Card className="bg-[#131924] border-[#212b3d] p-10 col-span-full text-center text-slate-500">
            <HardHat className="mx-auto mb-3 opacity-50" size={32} />
            Нет объектов по выбранному фильтру.
          </Card>
        )}
        {filtered.map((o) => (
          <button
            key={o.id}
            data-testid={`object-card-${o.id}`}
            onClick={() => nav(`/objects/${o.id}`)}
            className="group text-left rounded-xl border border-[#212b3d] bg-[#131924] hover:bg-[#161d2b] hover:border-amber-500/30 transition overflow-hidden"
          >
            {o.photos?.[0] ? (
              <div className="h-40 overflow-hidden bg-[#0f1521]">
                <img src={o.photos[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center bg-gradient-to-br from-[#0f1521] to-[#131924] text-slate-700">
                <HardHat size={40} />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-display font-semibold text-base text-slate-100 line-clamp-2">{o.title}</h3>
                <StatusBadge status={o.status} />
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1 mb-3">
                <MapPin size={12} /> {o.postal_code} {o.city}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(o.gewerke || []).slice(0, 3).map((g) => (
                  <Chip key={g} className="bg-[#0f1521] text-slate-300 border-[#212b3d]">{gewerkRu(g)}</Chip>
                ))}
                {(o.gewerke || []).length > 3 && (
                  <Chip className="bg-[#0f1521] text-slate-500 border-[#212b3d]">+{o.gewerke.length - 3}</Chip>
                )}
              </div>
              {isAdmin && o.revenue_planned !== undefined && (
                <div className="text-xs text-slate-500 flex items-center justify-between">
                  <span>План: <span className="text-slate-300 font-medium">{fmtEur(o.revenue_planned)}</span></span>
                  <span>Факт: <span className="text-emerald-400 font-medium">{fmtEur(o.revenue_actual)}</span></span>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium border transition ${
        active
          ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
          : "bg-[#131924] text-slate-400 border-[#212b3d] hover:bg-[#1c2434]"
      }`}
    >
      {children}
    </button>
  );
}

function CreateObjectDialog({ clients, workers, onCreated }) {
  const [form, setForm] = useState({
    title: "", client_id: "", address: "", postal_code: "", city: "",
    gewerke: [], status: "Anfrage", assigned_worker_ids: [],
    budget: 0, revenue_planned: 0, start_date: "", end_date: "",
  });
  const [saving, setSaving] = useState(false);

  const toggleG = (g) => setForm((f) => ({
    ...f, gewerke: f.gewerke.includes(g) ? f.gewerke.filter((x) => x !== g) : [...f.gewerke, g],
  }));
  const toggleW = (id) => setForm((f) => ({
    ...f, assigned_worker_ids: f.assigned_worker_ids.includes(id) ? f.assigned_worker_ids.filter((x) => x !== id) : [...f.assigned_worker_ids, id],
  }));

  const save = async () => {
    if (!form.title || !form.client_id) return alert("Введите название и выберите клиента");
    setSaving(true);
    try {
      await api.post("/objects", { ...form, budget: Number(form.budget) || 0, revenue_planned: Number(form.revenue_planned) || 0 });
      onCreated();
    } catch (e) {
      alert("Ошибка: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-display">Новый объект</DialogTitle></DialogHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label>Название *</Label>
          <Input data-testid="new-object-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                 className="bg-[#0f1521] border-[#212b3d]" />
        </div>
        <div>
          <Label>Клиент *</Label>
          <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
            <SelectTrigger data-testid="new-object-client" className="bg-[#0f1521] border-[#212b3d]"><SelectValue placeholder="Выберите клиента" /></SelectTrigger>
            <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Статус</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-[#0f1521] border-[#212b3d]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
              {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label_ru}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Адрес</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Индекс</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
          <div><Label>Город</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
        </div>
        <div className="sm:col-span-2">
          <Label>Виды работ</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {GEWERKE.map((g) => (
              <button key={g.de} type="button" onClick={() => toggleG(g.de)}
                data-testid={`gewerk-${g.de}`}
                className={`text-xs px-3 py-1.5 rounded-md border transition ${
                  form.gewerke.includes(g.de)
                    ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                    : "bg-[#0f1521] text-slate-400 border-[#212b3d] hover:bg-[#1c2434]"
                }`}
              >{g.de} <span className="text-slate-500">· {g.ru}</span></button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label>Назначить мастеров</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {workers.filter(w => w.role === "worker").map((w) => (
              <button key={w.id} type="button" onClick={() => toggleW(w.id)}
                className={`text-xs px-3 py-1.5 rounded-md border transition ${
                  form.assigned_worker_ids.includes(w.id)
                    ? "bg-blue-500/15 text-blue-300 border-blue-500/40"
                    : "bg-[#0f1521] text-slate-400 border-[#212b3d] hover:bg-[#1c2434]"
                }`}
              >{w.name}</button>
            ))}
          </div>
        </div>
        <div><Label>Бюджет (себестоимость), €</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
        <div><Label>Планируемый оборот, €</Label><Input type="number" value={form.revenue_planned} onChange={(e) => setForm({ ...form, revenue_planned: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" /></div>
      </div>
      <DialogFooter>
        <Button data-testid="save-object-button" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
          {saving ? "Сохранение…" : "Создать объект"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
